<script type="text/jsx" lang="jsx">
import { toUpper } from 'lodash'
import { mapMutations } from "vuex";
import Tile from '@/components/tile.vue';
import { isEduSchCmpPlusContext, ZOOM_MEETING_SUB_TYPES } from '@/plans/ZOOM_MEETING/common/utils';
import { AutoExpireBadge } from '@zoom/fe-billing-components';
import planFeaturesMixin from "@/utils/planFeaturesMixin";
import { isBrickMortarJointSalePartner } from '@/utils/partner';
import { setExtraContext4AddOn } from '@/aspects/trackings';
import { translateToEn } from '@/plugins/i18n';
import { ADDON_GROUP, planTypes } from "@/common/constants";
import { getAddonLearnMoreMap } from '@/common/utils';

export default {
  components: {
    Tile,
  },
  mixins: [planFeaturesMixin],
  props: {
    isSuccessPage: {
      type: Boolean,
      default: false
    },
    group: {
      type: String,
      default: ADDON_GROUP.DEFAULT,
    },
  },
  data() {
    return {
      logo: '',
      title: '',
      subtitle: '',
      description: '',
      addText: this.$t('billing.common.add'),
      addTrackingText: translateToEn('billing.common.add'),
      isNewAddOn: false, // private
      headMarginRight: '200px',
      isFreeTrial: false,
      promo: '',
      freeTrialDays: 0,
      productPlans: {}
    };
  },
  watch: {
    isFreeTrialComputed(val) {
      if (val) {
        if (!this.promo) {
          this.promo = toUpper(this.$t('billing.trial.free_trial'))
        }
        this.addText = this.$t('billing.trial.try_for_n_days', [this.freeTrialDays])
      } else {
        this.promo = ''
        this.addText = this.$t('billing.common.add')
      }
      this.$nextTick(() => {
        this.setHeadMarginRight()
      })
    }
  },
  computed: {
    isFreeTrialComputed() {
      return this.isFreeTrial;
    },
    learnMoreItem() {
      const addonLearnMoreMap = getAddonLearnMoreMap();
      if (this.planType === planTypes.ZOOM_MEETING) {
        if (isBrickMortarJointSalePartner() && this.category === 'pro') {
          return {
            ...addonLearnMoreMap[this.category],
            hideOptions: false
          };
        }
        return addonLearnMoreMap[this.category];
      } else if (this.planType === planTypes.ZOOM_WHITEBOARD) {
        return addonLearnMoreMap[this.category];
      } else {
        return addonLearnMoreMap[this.planType];
      }
    },
    enableCarouselAddonList() {
      return window.$isEnabled('enableCarouselAddonList');
    }
  },
  methods: {
    ...mapMutations(['setLearnMoreConfig']),
    /**
     * handle addOn logic: before/after executing addOn, handle various common logic
     * @param {Object} context - optional context information, such as tracking data, business parameters, etc.
     *
     * The extensible common logic includes:
     * - Set tracking context
     */
    handleAddOn () {
      // Set context information before calling addOn
      if (this.group === ADDON_GROUP.WIN_BACK) {
        setExtraContext4AddOn({ uiLocation: 'previously purchased' });
      }

      // Execute actual addOn operation
      this.addOn();
    },
    handleAddFreeTrial() {
      // Set context information before calling addOn
      if (this.group === ADDON_GROUP.WIN_BACK) {
        setExtraContext4AddOn({ uiLocation: 'previously purchased' });
      }

      // Execute actual addFreeTrial operation
      this.addFreeTrial();
    },
    addOn() {
      // addPlanFromEnterKey For A11Y requirement
      // target is BUTTON comes from keyboard enter, will auto focaus on the first option in config card
      // target is SPAN comes from click
      document.addPlanFromEnterKey = true;
      this.$emit('add', this.$options?.key);

      if (isEduSchCmpPlusContext || (window?.$config('plan') === ZOOM_MEETING_SUB_TYPES.ZONE_EDU_SCH_CMP)) {
        this.$tracking.buyInteraction(`zoom one for education school and campus${isEduSchCmpPlusContext ? ' plus' : ''} add-ons`, `${this.title?.toLowerCase()} - add`);
      } else {
        this.$tracking.addOnBuyInteraction(`${this.trackingBasePlanName} add-ons`, `add-ons - ${this.title?.toLowerCase()} - ${this.trackingAddAction}`);
      }
    },
    add2cart() {
      this.$emit('add', this.$options?.key);
    },
    addFreeTrial() {
      this.addOn()
    },
    mx_logo() {
      if (!this.logo) return;
      return <div class="opc-addon__logo">
        <img src={this.logo} class="plan-icon plan-icon__default" alt={`${this.title} Icon`} tabindex="-1" />
      </div>;
    },
    mx_title() {
      return <h3 class="opc-addon__title">
        {this.promo && this.is_mobile_reactive ? <zm-badge text={this.promo} color="green" /> : null}
        {this.title + ' '}
        <AutoExpireBadge class="opc-addon__new-flag" badgeId={this.planType} theme="plan" onActive={() => this.isNewAddOn = true} />
        {this.promo && !this.is_mobile_reactive? <zm-badge text={this.promo} color="green" /> : null}
      </h3>;
    },
    mx_subTitle() {
      return this.subtitle ? <div class="opc-addon__subtitle">
        {this.subtitle}
        <priceVATTip />
      </div> : null;
    },
    mx_titleBox() {
      return <div class="opc-addon__title-box">
        {this.mx_title()}
        {this.mx_subTitle()}
      </div>;
    },
    mx_action() {
      let ariaLabel = ''
      if (this.isNewAddOn) {
        ariaLabel = `Add ${this.title} ${this.promo??''} New`
      } else if (this.isFreeTrialComputed) {
        ariaLabel = `Try ${this.title} for ${this.freeTrialDays} days`
      } else {
        ariaLabel = `${this.addText} ${this.title} ${this.promo??''}`
      }
      return <div class="opc-addon__action" ref="actionDom">
        {this.hasLearnMore() &&
          <zm-button
            type="link"
            aria-label={this.$t('billing.common.learn_more_about', [this.title])}
            class="opc-addon__action__learn_more"
            onClick={this.handleLearnMore}
          >
            {this.$t('common.learnMore')}
          </zm-button>
        }
        <zm-button
          type="secondary"
          data-testid={`checkout-button-addon-add-${this.planType}`}
          ref="addBtn"
          aria-label={ariaLabel}
          onClick={this.isSuccessPage ? this.add2cart : (this.isFreeTrialComputed ? this.handleAddFreeTrial : this.handleAddOn)}>
          {this.addText}
        </zm-button>
        {this.isFreeTrialComputed &&
          <div class="opc-addon__action-sub">
            <zm-button data-testid={`checkout-button-addon-buy-${this.planType}`} type="link" onClick={this.addOn} aria-label={`or buy ${this.title} now`}>
              {this.$t('billing.trial.or_buy_now')}
            </zm-button>
          </div>
        }
      </div>;
    },
    mx_head() {
      return <div class="opc-addon__main">
        <div class="opc-addon__head" style={{marginRight: this.headMarginRight}}>
          {this.mx_logo()}
          {this.mx_titleBox()}
        </div>
        {this.mx_desc()}
      </div>;
    },
    mx_desc() {
      return <div class="opc-addon__description first-capitalize" style={{marginRight: this.isFreeTrialComputed ? this.headMarginRight : 0}}>
        {this.description}
      </div>;
    },
    isVisible(element) {
      return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
    },
    setHeadMarginRight() {
      const actionDom = this.$refs.actionDom;
      // non-mobile need to set margin right (action dom position is absolute)
      // why don't use 'this.$context.is.mobile' because it's not accurate
      // iPad mini is mobile but not need to set margin right
      if (actionDom && window.getComputedStyle(actionDom).position === 'absolute') {
        this.headMarginRight = `${actionDom.offsetWidth + 8}px`;
      } else {
        this.headMarginRight = 'auto';
      }
    },
    hasLearnMore() {
      if (this.isFreeTrialComputed || this.isSuccessPage) return false;
      return !!this.learnMoreItem;
    },
    handleLearnMore() {
      const modalConfig = this.learnMoreItem;
      if (!modalConfig) return;

      const learnMoreConfig = {
        visible: true,
        planType: modalConfig.dialogName || this.planType,
        categoryCode: null,
        productPlans: this.productPlans,
        hideOptions: typeof this.hideLearnMoreDialogOptions === 'boolean' ? this.hideLearnMoreDialogOptions : !!modalConfig.hideOptions,
        addAction: this.addOn,
        buyCta: this.addText,
        changeAction: this.onLearnMoreDialogChange,
        trackingBasePlanName: this.trackingBasePlanName,
        trackingPlanTitle: this.trackingPlanName || this.title,
        title: modalConfig.title,
        tag: this.promo
      }

      this.setLearnMoreConfig(learnMoreConfig);
    }
  },
  render() {
    const uniqueClass = `opc-addon--${this.planType}`;
    const classList = ['opc-addon', uniqueClass, this.logo ? 'has-logo' : '', this.isFreeTrialComputed ? 'opc-addon__free-trial' : '', this.promo ? 'opc-addon__has-promo' : ''];

    return <tile class={classList} {...this.$attrs}>
      {this.enableCarouselAddonList && <div class="opc-addon__banner-promo">{this.promo}</div>}
      <div class="opc-addon__container">
        {this.mx_head()}
        {this.mx_action()}
      </div>
    </tile>
  },
  mounted() {
    this.setHeadMarginRight()

    // [ZTSPZ-306][AB-ECOMM] 2024.5 - OPC Confirmation Page - Addons
    // Influenced by Optimizely campaigns
    if (this.$config('isNewSuccessPage') && this.is_mobile_reactive) {
      let index = 0;
      let timer = setInterval(() => {
        try {
          if (index >= 10) {
            clearInterval(timer);
          }
          index++;
          const isVisible = this.isVisible(this.$refs.addBtn.$el);
          if (isVisible) {
            this.setHeadMarginRight()
            clearInterval(timer);
          }
        } catch {
          clearInterval(timer);
        }
      }, 20);
    }
  }
};
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';

$logo-width: 44px;
$logo-width-mobile: 32px;
$gap: 16px;
.opc-addon {
  .plan-icon {
    &__default {
      width: $logo-width;
      height: $logo-width;
      @include media(mobile, tablet) {
        width: $logo-width-mobile;
        height: $logo-width-mobile;
      }
    }
  }

  &__container {
    position: relative;
    width: 100%;
    height: 100%;
  }

  &__main {
    display: flex;
    flex-direction: column;
  }

  &__head {
    display: flex;
  }
  &__logo {
    flex-shrink: 0;
    width: $logo-width;
    height: $logo-width;
    font-size: 0;
    margin-right: $gap;
    @include media(mobile, tablet) {
      margin-right: 8px;
      width: $logo-width-mobile;
      height: $logo-width-mobile;
    }
    img {
      width: 100%;
      height: 100%;
    }
  }
  &__title-box {
    flex: 1;
  }
  &__title {
    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    font-size: 20px;
    font-style: normal;
    font-weight: 590;
    line-height: 24px;

    @include media(mobile,tablet){
      font-size: 16px;
    }
    &__promo{
      color: white;
      white-space: nowrap;
      font-size: 14px;
      padding: 2px 4px;
      font-weight: normal;
      vertical-align: middle;
      border-radius: 100px;
      background: linear-gradient(78deg, #493AB7 0%, #CF4B0A 100%);
      @include media(mobile,tablet){
        border-radius: 100px;
        font-size: 14px;
        padding: 2px 4px;
      }
      @include media(mobile){
        margin-left: 0px;
      }
      &.opc-addon__promo-freetrial {
        background: linear-gradient(78deg, #493ab7 0%, #925dff 100%)
      }
    }
    .zbo-badge{
      vertical-align: middle;
    }
  }
  &__new-flag {
    margin-left: 5px;
    text-transform: uppercase;
    @include media(mobile, tablet) {
      font-size: 12px;
      width: auto;
    }
  }
  &__action {
    position: absolute;
    top: 0;
    right: 0;
    text-align: end;

    @include media(mobile) {
      position: relative;
      text-align: right;
      margin-top: 6px;
    }

    button {
      // border: 1px solid $theme-color;
      // color: var(--zoom-color-blue-default, $theme-color);

      &.opc-addon__action__learn_more {
        border: none;
        margin-right: 8px;
      }
    }
    :deep(.zbo-icons) {
      width: 12px;
      height: 12px;
      margin-left: 4px;
    }

    &-sub {
      margin-top: 19px;
      button {
        border: none
      }
    }
  }
  &__subtitle {
    text-align: left;

    color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
  }
  &__description {
    margin-top: 6px;

    color: var(--zoom-color-text-neutral, #686F79);
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;

    @include media(mobile, tablet) {
      margin-top: 8px;
      font-weight: 400;
    }
  }
  &.has-logo {
    .opc-addon__description {
      padding-left: $logo-width + $gap;
      @include media(mobile,tablet){
        padding-left: 0px;
      }
    }
  }
  &.opc-addon__free-trial {
    .opc-addon__subtitle {
      @include media(mobile, tablet) {
        margin-right: 30px;
      }
    }
  }
}

</style>
